import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { usePageMeta } from '../../../../hooks/usePageMeta';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Header from '../../../../components/2025/Header';
import Footer from '../../../../components/2025/Footer';
import HeroTitle from '../../../../components/2025/HeroTitle';
import EventCard from '../../../../components/2025/EventCard';
import EventFilterNav from '../../../../components/2025/events/EventFilterNav';
import SchoolFilterSidebar from '../../../../components/2025/events/SchoolFilterSidebar';
import LoadingSpinner from '../../../../components/2025/LoadingSpinner';
import { fetchAndCategorizeEvents, CategorizedEvents } from '../../../../services/eventService';
import { event } from '../../../../types/cms-types';
import { optimizeImage } from '../../../../utils/imageOptimizer';
import Styles from './styles.module.scss';

const convertEventToEventData = (event: event) => ({
  imageUrl: optimizeImage(event.event_image.url, { width: 800, quality: 80 }),
  title: event.event_title,
  time: event.event_time,
  location: event.event_location,
  description: event.event_description,
  labels: [
    ...event.event_category,
    ...event.event_day,
    ...(event.school || []),
    ...(event.faculties || []),
    ...(event.department || []),
  ],
});

const EventsNeec2025: React.FC = () => {
  const [categorizedEvents, setCategorizedEvents] = useState<CategorizedEvents | null>(null);
  const [filteredEvents, setFilteredEvents] = useState<event[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<event | null>(null);
  const scrollPositionRef = useRef<number | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const schoolName = '日本工学院';

  usePageMeta({
    title: 'イベント一覧 - 日本工学院専門学校',
    description:
      'かまた祭2025の日本工学院専門学校のイベント一覧。学部・学科ごとにイベントを絞り込んで閲覧できます。',
  });

  const handleFilterChange = useCallback(
    (selectedFaculties: string[], selectedDepartments: string[]) => {
      if (!categorizedEvents) return;

      // 重複を除去してユニークなイベントのみを取得
      const allEvents = Object.values(categorizedEvents).flat();
      const uniqueEventsMap = new Map<string, event>();
      allEvents.forEach((event) => {
        uniqueEventsMap.set(event.id, event);
      });
      const uniqueEvents = Array.from(uniqueEventsMap.values());
      const schoolEvents = uniqueEvents.filter((event) => event.school?.includes(schoolName));

      if (selectedFaculties.length === 0 && selectedDepartments.length === 0) {
        setFilteredEvents(schoolEvents);
        return;
      }

      const filtered = schoolEvents.filter((event) => {
        const eventFaculties = event.faculties || [];
        const eventDepartments = event.department || [];

        // 学部でマッチング
        const hasFaculty =
          selectedFaculties.length > 0 &&
          selectedFaculties.some((faculty) => {
            const parts = faculty.split('-');
            const facultyName = parts.length > 1 ? parts.slice(1).join('-') : faculty;
            return eventFaculties.some((f) => f === facultyName);
          });

        // 学科でマッチング
        const hasDepartment =
          selectedDepartments.length > 0 &&
          selectedDepartments.some((department) => {
            const parts = department.split('-');
            const departmentName = parts.length > 2 ? parts[2] : department;
            return eventDepartments.some((dept) => dept === departmentName);
          });

        return hasFaculty || hasDepartment;
      });

      setFilteredEvents(filtered);
    },
    [categorizedEvents, schoolName],
  );

  const handleSelectEvent = (event: event) => {
    setSelectedEvent(event);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('event', event.id);
    navigate(`?${newSearchParams.toString()}`, { replace: true });
  };

  const handleCloseModal = () => {
    const savedScrollY = scrollPositionRef.current;

    setSelectedEvent(null);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('event');
    const paramString = newSearchParams.toString();
    navigate(paramString ? `?${paramString}` : '', { replace: true });

    requestAnimationFrame(() => {
      const body = document.body;
      const html = document.documentElement;
      body.style.position = '';
      body.style.top = '';
      body.style.left = '';
      body.style.right = '';
      body.style.width = '';
      body.style.overflow = '';
      html.style.overflow = '';

      if (savedScrollY !== null) {
        window.scrollTo(0, savedScrollY);
        scrollPositionRef.current = null;
      }
    });
  };

  useEffect(() => {
    const eventId = searchParams.get('event');
    if (eventId && categorizedEvents) {
      const allEvents = Object.values(categorizedEvents).flat();
      const foundEvent = allEvents.find((e) => e.id === eventId);
      if (foundEvent) {
        setSelectedEvent(foundEvent);
      }
    }
  }, [searchParams, categorizedEvents]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const body = document.body;
    const html = document.documentElement;

    if (selectedEvent) {
      if (scrollPositionRef.current === null) {
        const scrollY = window.scrollY || html.scrollTop || 0;
        scrollPositionRef.current = scrollY;

        body.style.position = 'fixed';
        body.style.top = `-${scrollY}px`;
        body.style.left = '0';
        body.style.right = '0';
        body.style.width = '100%';
        body.style.overflow = 'hidden';
        html.style.overflow = 'hidden';
      }
    }
  }, [selectedEvent]);

  useEffect(() => {
    const loadEvents = async () => {
      try {
        setIsLoading(true);
        const events = await fetchAndCategorizeEvents();
        setCategorizedEvents(events);

        // 重複を除去してユニークなイベントのみを取得
        const allEvents = Object.values(events).flat();
        const uniqueEventsMap = new Map<string, event>();
        allEvents.forEach((event) => {
          uniqueEventsMap.set(event.id, event);
        });
        const uniqueEvents = Array.from(uniqueEventsMap.values());

        console.log('=== NEEC Debug ===');
        console.log('Looking for school:', schoolName);
        console.log('Total unique events:', uniqueEvents.length);

        // 全イベントの学校名を確認
        const schoolNames = new Set<string>();
        uniqueEvents.forEach((event) => {
          if (event.school) {
            event.school.forEach((s) => schoolNames.add(s));
          }
        });
        console.log('Available school names:', Array.from(schoolNames));

        const schoolEvents = uniqueEvents.filter((event) => {
          const hasSchool = event.school?.some((s) => s.includes(schoolName));
          if (hasSchool) {
            console.log('Found event:', event.event_title, 'School:', event.school);
          }
          return hasSchool;
        });
        console.log('Filtered school events:', schoolEvents.length);

        setFilteredEvents(schoolEvents);
        setError(null);
      } catch (err) {
        console.error('イベントデータの取得に失敗しました:', err);
        setError('イベントデータの読み込みに失敗しました');
      } finally {
        setIsLoading(false);
      }
    };

    loadEvents();
  }, [schoolName]);

  return (
    <>
      <Header />
      <main className={Styles.events}>
        <HeroTitle title="EVENT" subtitle="日本工学院専門学校" />
        <EventFilterNav />

        {isLoading ? (
          <LoadingSpinner />
        ) : error ? (
          <div className={Styles.error}>
            <p>{error}</p>
          </div>
        ) : (
          <section className={Styles.eventSection}>
            <div className={Styles.eventSection__container}>
              <div className={Styles.eventSection__layout}>
                <SchoolFilterSidebar schoolName={schoolName} onChange={handleFilterChange} />

                <div className={Styles.eventSection__main}>
                  {filteredEvents.length > 0 ? (
                    <div className={Styles.categorySection__grid}>
                      {filteredEvents.map((event) => (
                        <div
                          key={event.id}
                          className={Styles.categorySection__card}
                          onClick={() => handleSelectEvent(event)}
                        >
                          <div className={Styles.categorySection__imageWrapper}>
                            <div
                              className={Styles.categorySection__imageBg}
                              style={{
                                backgroundImage: `url(${optimizeImage(event.event_image.url, { width: 800, quality: 75 })})`,
                              }}
                            />
                            <img
                              src={optimizeImage(event.event_image.url, {
                                width: 800,
                                quality: 80,
                              })}
                              alt={event.event_title}
                              className={Styles.categorySection__image}
                            />
                          </div>
                          <div className={Styles.categorySection__content}>
                            <h3 className={Styles.categorySection__eventTitle}>
                              {event.event_title}
                            </h3>
                            <p className={Styles.categorySection__location}>
                              {event.event_location}
                            </p>
                            <div className={Styles.categorySection__footer}>
                              <div className={Styles.categorySection__dateTags}>
                                {event.event_day.map((day: string, index: number) => (
                                  <span
                                    key={`${event.id}-day-${index}`}
                                    className={Styles.categorySection__dateTag}
                                  >
                                    #{day}
                                  </span>
                                ))}
                                {event.faculties?.map((faculty: string, index: number) => (
                                  <span
                                    key={`${event.id}-faculty-${index}`}
                                    className={Styles.categorySection__dateTag}
                                  >
                                    #{faculty}
                                  </span>
                                ))}
                                {event.department?.map((dept: string, index: number) => (
                                  <span
                                    key={`${event.id}-dept-${index}`}
                                    className={Styles.categorySection__dateTag}
                                  >
                                    #{dept}
                                  </span>
                                ))}
                              </div>
                              <svg
                                width="25"
                                height="25"
                                viewBox="0 0 25 25"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                className={Styles.categorySection__arrow}
                              >
                                <circle
                                  cx="12.5"
                                  cy="12.5"
                                  r="12.5"
                                  transform="rotate(90 12.5 12.5)"
                                  fill="#0946F1"
                                />
                                <path
                                  d="M7.30859 12.5137L16.833 12.6504"
                                  stroke="white"
                                  strokeLinecap="round"
                                />
                                <path
                                  d="M12.5625 8.8457L17.3071 12.6413"
                                  stroke="white"
                                  strokeLinecap="round"
                                />
                                <path
                                  d="M12.5625 16.4441L17.3071 12.6484"
                                  stroke="white"
                                  strokeLinecap="round"
                                />
                              </svg>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={Styles.comingSoon}>
                      <p className={Styles.comingSoon__text}>Coming Soon...</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>
        )}
        <EventFilterNav />
      </main>
      <Footer />
      {selectedEvent &&
        createPortal(
          <div
            className={Styles.modal}
            onClick={handleCloseModal}
            onTouchMove={(e) => e.preventDefault()}
          >
            <div className={Styles.modal__content} onClick={(e) => e.stopPropagation()}>
              <EventCard
                events={[convertEventToEventData(selectedEvent)]}
                onClose={handleCloseModal}
                className={Styles.eventModal}
              />
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default EventsNeec2025;
